import React from 'react';
import styled from 'styled-components';

const KeyFeatures = () => {
  return (
    <SectionContainer>
      <SectionInner>
        <SectionTitleContainer>
          <SectionTag>Features</SectionTag>
          <SectionTitle>Essential Tools</SectionTitle>
        </SectionTitleContainer>
        
        <FeaturesGrid>
          {/* Workout Planning Feature with Graph Overlay */}
          <FeatureCard>
            <FeatureImage src="/images/MedicineBall.jpeg" alt="AI Workout Planning" />
            <MetricsOverlay>
              <MinimalistGraph>
                <GraphTitle>Body Fat %</GraphTitle>
                <GraphLine>
                  <GraphYAxis>
                    <GraphYValue>40%</GraphYValue>
                    <GraphYValue>30%</GraphYValue>
                    <GraphYValue>20%</GraphYValue>
                    <GraphYValue>10%</GraphYValue>
                  </GraphYAxis>
                  <GraphLineVisual>
                    <path 
                        d="M10,20 C40,30 80,65 120,95 S150,110 180,115" 
                        fill="none" 
                        stroke="#BA865E" 
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </GraphLineVisual>
                </GraphLine>
              </MinimalistGraph>
            </MetricsOverlay>
            <FeatureContent>
              <FeatureTitle>AI Workout Planning</FeatureTitle>
              <FeatureDescription>
                Automatically generate personalized workout plans based on client goals.
              </FeatureDescription>
            </FeatureContent>
          </FeatureCard>
          
          {/* Nutrition Tracking Feature with minimal, elegant design */}
          <FeatureCard>
            <FeatureImage src="/images/SteakPhoto.jpeg" alt="Nutrition Tracking" />
            <MetricsOverlay>
              <NutritionMetricsLayout>
                <NutritionMetricCard className="protein-card" style={{ top: '-10%', left: '15%' }}>
                  <NutritionMetricLabel>Protein</NutritionMetricLabel>
                  <NutritionMetricCircleContainer>
                    <NutritionMetricCircleSVG viewBox="0 0 36 36">
                      <NutritionMetricBackCircle cx="18" cy="18" r="16" />
                      <NutritionMetricProgressCircle 
                        cx="18" 
                        cy="18" 
                        r="16" 
                        color="rgba(200, 115, 115, 0.8)"
                        circumference={100.53} 
                        progress={65} 
                      />
                    </NutritionMetricCircleSVG>
                    <NutritionMetricValue>11g</NutritionMetricValue>
                  </NutritionMetricCircleContainer>
                </NutritionMetricCard>
                
                <NutritionMetricCard className="carbs-card" style={{ top: '5%', left: '43%' }}>
                  <NutritionMetricLabel>Carbs</NutritionMetricLabel>
                  <NutritionMetricCircleContainer>
                    <NutritionMetricCircleSVG viewBox="0 0 36 36">
                      <NutritionMetricBackCircle cx="18" cy="18" r="16" />
                      <NutritionMetricProgressCircle 
                        cx="18" 
                        cy="18" 
                        r="16" 
                        color="rgba(235, 165, 75, 0.8)"
                        circumference={100.53} 
                        progress={80} 
                      />
                    </NutritionMetricCircleSVG>
                    <NutritionMetricValue>93g</NutritionMetricValue>
                  </NutritionMetricCircleContainer>
                </NutritionMetricCard>
                
                <NutritionMetricCard className="fat-card" style={{ top: '20%', right: '8%' }}>
                  <NutritionMetricLabel>Fat</NutritionMetricLabel>
                  <NutritionMetricCircleContainer>
                    <NutritionMetricCircleSVG viewBox="0 0 36 36">
                      <NutritionMetricBackCircle cx="18" cy="18" r="16" />
                      <NutritionMetricProgressCircle 
                        cx="18" 
                        cy="18" 
                        r="16" 
                        color="rgba(115, 175, 115, 0.8)"
                        circumference={100.53} 
                        progress={45} 
                      />
                    </NutritionMetricCircleSVG>
                    <NutritionMetricValue>21g</NutritionMetricValue>
                  </NutritionMetricCircleContainer>
                </NutritionMetricCard>
              </NutritionMetricsLayout>
            </MetricsOverlay>
            <FeatureContent>
              <FeatureTitle>Nutrition Tracking</FeatureTitle>
              <FeatureDescription>
                Smart meal planning and nutrition tracking with personalized recommendations.
              </FeatureDescription>
            </FeatureContent>
          </FeatureCard>
          
          {/* Habit Formation Feature with Weekly Progress */}
          <FeatureCard className="habit-formation">
            <FeatureImage src="/images/RunningImage.jpeg" alt="Habit Formation" />
            <WeeklyGoalsOverlay>
              <WeeklyProgressContainer>
                <WeeklyProgressTitle>Weekly Goals</WeeklyProgressTitle>
                <WeeklyProgressGrid>
                  <WeekDay completed>
                    <WeekDayLabel>M</WeekDayLabel>
                    <CheckIcon completed />
                  </WeekDay>
                  <WeekDay completed>
                    <WeekDayLabel>T</WeekDayLabel>
                    <CheckIcon completed />
                  </WeekDay>
                  <WeekDay completed>
                    <WeekDayLabel>W</WeekDayLabel>
                    <CheckIcon completed />
                  </WeekDay>
                  <WeekDay>
                    <WeekDayLabel>T</WeekDayLabel>
                    <CheckIcon />
                  </WeekDay>
                  <WeekDay>
                    <WeekDayLabel>F</WeekDayLabel>
                    <CheckIcon />
                  </WeekDay>
                  <WeekDay>
                    <WeekDayLabel>S</WeekDayLabel>
                    <CheckIcon />
                  </WeekDay>
                  <WeekDay>
                    <WeekDayLabel>S</WeekDayLabel>
                    <CheckIcon />
                  </WeekDay>
                </WeeklyProgressGrid>
              </WeeklyProgressContainer>
            </WeeklyGoalsOverlay>
            <FeatureContent>
              <FeatureTitle>Habit Formation</FeatureTitle>
              <FeatureDescription>
                Build lasting behavior change through personalized habit protocols.
              </FeatureDescription>
            </FeatureContent>
          </FeatureCard>
          
          {/* Client Messaging Feature */}
          <FeatureCard className="messaging">
            <ColorBackground />
            <PhoneMockupOverlay>
              <PhoneContent>
                <PhoneScreenshot src="/images/MessagingScreenshotNew.png" alt="App messaging interface" />
              </PhoneContent>
            </PhoneMockupOverlay>
            <FeatureContent style={{ background: 'none' }}>
              <FeatureTitle style={{ color: 'white', textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)' }}>Client Messaging</FeatureTitle>
              <FeatureDescription style={{ color: 'rgba(255, 255, 255, 0.9)', textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)' }}>
                AI-powered communication that maintains your voice with every client.
              </FeatureDescription>
            </FeatureContent>
          </FeatureCard>
        </FeaturesGrid>
      </SectionInner>
    </SectionContainer>
  );
};

const SectionContainer = styled.section`
  padding: 12rem 6rem;
  background-color: #FBFBFB;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 8rem 2rem;
  }
`;

const SectionInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitleContainer = styled.div`
  text-align: center;
  margin-bottom: 6rem;
`;

const SectionTag = styled.div`
  font-family: 'SF Pro Text', 'Plus Jakarta Sans', sans-serif;
  font-size: 13px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #BA865E;
  margin-bottom: 1rem;
  background: linear-gradient(90deg, #BA865E 0%, #C4845F 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SectionTitle = styled.h2`
  font-family: 'SF Pro Display', 'Satoshi', sans-serif;
  font-size: 48px;
  font-weight: 300;
  color: #1D1D1F;
  letter-spacing: -0.03em;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2.5rem;
  width: 100%;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 3.5rem;
  }
`;

const FeatureCard = styled.div`
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  height: 500px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.03);
  
  /* More subtle gradient for standard cards */
  background: linear-gradient(180deg, 
    rgba(0, 0, 0, 0) 0%, 
    rgba(0, 0, 0, 0.01) 75%, 
    rgba(0, 0, 0, 0.04) 90%, 
    rgba(0, 0, 0, 0.08) 100%);
    
  /* Special handling for habit formation card */
  &.habit-formation {
    background: linear-gradient(180deg, 
      rgba(0, 0, 0, 0) 0%, 
      rgba(0, 0, 0, 0) 80%, 
      rgba(0, 0, 0, 0.03) 90%, 
      rgba(0, 0, 0, 0.05) 100%);
  }
  
  /* Remove background for messaging card as it has its own */
  &.messaging {
    background: none;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.05);
  }
  
  @media (max-width: 900px) {
    height: 600px; /* Increased height for more space on mobile */
  }
`;

const FeatureImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const FeatureContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 2rem;
  /* More subtle gradient for text area */
  background: linear-gradient(0deg, 
    rgba(0,0,0,0.7) 0%, 
    rgba(0,0,0,0.5) 50%,
    rgba(0,0,0,0.1) 90%, 
    rgba(0,0,0,0) 100%);
  color: white;
  z-index: 3;
  
  @media (max-width: 900px) {
    padding-bottom: 2.5rem; /* Add more padding at the bottom on mobile */
  }
`;

const FeatureTitle = styled.h3`
  font-family: 'SF Pro Display', 'Satoshi', sans-serif;
  font-size: 28px;
  font-weight: 500;
  margin-bottom: 0.8rem;
  letter-spacing: -0.02em;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
`;

const FeatureDescription = styled.p`
  font-family: 'SF Pro Text', 'Plus Jakarta Sans', sans-serif;
  font-size: 16px;
  line-height: 1.5;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
  max-width: 90%;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.15);
`;

const PhoneMockupOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
  padding: 2rem;
  pointer-events: none;
  
  @media (max-width: 900px) {
    align-items: flex-start;
    padding-top: 2rem;
  }
`;

const PhoneContent = styled.div`
  width: 220px;
  height: auto;
  position: relative;
  margin: 0 auto;
  padding: 1rem;
  margin-top: -8rem;
  filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.3));
  
  @media (max-width: 900px) {
    margin-top: 2rem; /* Changed from -3rem to positive value to move phone mockup down */
  }
`;

const PhoneScreenshot = styled.img`
  width: 100%;
  height: auto;
  display: block;
  filter: drop-shadow(0 12px 24px rgba(0, 0, 0, 0.2));
  border-radius: 24px;
`;

const FeatureOverlayElements = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const MetricsOverlay = styled(FeatureOverlayElements)`
  align-items: flex-start;
  padding-top: 4rem;
  
  @media (max-width: 900px) {
    padding-top: 8rem; /* Restored to original 8rem to keep body fat graph in original position */
    justify-content: center; /* Center horizontally */
  }
`;

const WeeklyGoalsOverlay = styled(FeatureOverlayElements)`
  align-items: center;
  
  @media (max-width: 900px) {
    align-items: flex-start;
    padding-top: 8rem; /* Increased from 5rem to move weekly goals further down */
  }
`;

const MinimalistGraph = styled.div`
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 1.5rem;
  width: 260px;
  border: 0.5px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
`;

const GraphTitle = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: white;
  margin-bottom: 1.5rem;
  text-align: center;
  font-family: 'SF Pro Text', 'Plus Jakarta Sans', sans-serif;
`;

const GraphLine = styled.div`
  display: flex;
  height: 120px;
  position: relative;
`;

const GraphYAxis = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-right: 10px;
  width: 40px;
`;

const GraphYValue = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
  text-align: right;
  font-family: 'SF Pro Text', 'Plus Jakarta Sans', sans-serif;
`;

const GraphLineVisual = styled.svg`
  position: relative;
  flex: 1;
  height: 120px;
  overflow: visible;
`;

const WeeklyProgressContainer = styled.div`
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 1.5rem;
  width: 280px;
  border: 0.5px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
`;

const WeeklyProgressTitle = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: white;
  margin-bottom: 1.5rem;
  text-align: center;
  font-family: 'SF Pro Text', 'Plus Jakarta Sans', sans-serif;
`;

const WeeklyProgressGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
`;

const WeekDay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 0;
  border-radius: 8px;
  background: ${props => props.completed ? 'rgba(186, 134, 94, 0.25)' : 'rgba(255, 255, 255, 0.08)'};
  transition: background-color 0.2s ease;
`;

const WeekDayLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: white;
  margin-bottom: 8px;
  font-family: 'SF Pro Text', 'Plus Jakarta Sans', sans-serif;
`;

const CheckIcon = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1.5px solid ${props => props.completed ? '#BA865E' : 'rgba(255, 255, 255, 0.25)'};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '';
    display: ${props => props.completed ? 'block' : 'none'};
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #BA865E;
  }
`;

const NutritionMetricsLayout = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  
  @media (max-width: 900px) {
    /* Arrange metrics vertically for mobile */
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding-top: 0; /* Keep at 0 to remove padding */
    height: 70%; /* Keep height increased */
    margin-top: -6rem; /* Added negative margin to move nutrition metrics up without affecting the container */
  }
`;

const NutritionMetricCard = styled.div`
  background-color: rgba(20, 20, 20, 0.8);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: 16px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 110px;
  height: 110px;
  position: absolute;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 0.5px solid rgba(255, 255, 255, 0.08);
  
  @media (max-width: 900px) {
    position: relative;
    width: 100px; /* Slightly larger for mobile */
    height: 100px; /* Slightly larger for mobile */
    margin-bottom: 8px; /* Reduced from 15px to keep metrics closer together */
    top: unset !important;
    left: unset !important;
    right: unset !important;
    
    &.protein-card {
      transform: translateY(0);
    }
    
    &.carbs-card {
      transform: translateY(0);
    }
    
    &.fat-card {
      transform: translateY(0);
    }
  }
`;

const NutritionMetricLabel = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.9);
  font-family: 'SF Pro Display', 'Satoshi', sans-serif;
  margin-bottom: 8px;
  letter-spacing: -0.01em;
`;

const NutritionMetricValue = styled.div`
  font-size: 20px;
  font-weight: 400;
  color: white;
  font-family: 'SF Pro Display', 'Satoshi', sans-serif;
  letter-spacing: -0.02em;
  
  @media (max-width: 900px) {
    font-size: 18px; /* Slightly smaller font for mobile */
  }
`;

const NutritionMetricCircleContainer = styled.div`
  position: relative;
  height: 70px;
  width: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 900px) {
    height: 70px; /* Keep the same as desktop */
    width: 70px; /* Keep the same as desktop */
  }
`;

const NutritionMetricCircleSVG = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
`;

const NutritionMetricBackCircle = styled.circle`
  fill: none;
  stroke: rgba(255, 255, 255, 0.08);
  stroke-width: 1.5;
`;

const NutritionMetricProgressCircle = styled.circle`
  fill: none;
  stroke: ${props => props.color || 'rgba(229, 115, 115, 0.8)'};
  stroke-width: 1.5;
  stroke-linecap: round;
  stroke-dasharray: ${props => props.circumference};
  stroke-dashoffset: ${props => props.circumference - (props.progress / 100) * props.circumference};
  transition: stroke-dashoffset 0.5s ease;
`;

const ColorBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #232323 0%, #121212 100%);
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 70% 20%, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 60%);
    z-index: 1;
  }
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0) 50%);
    z-index: 1;
  }
`;

export default KeyFeatures; 